import { useState } from "react";
import { Filter } from "lucide-react";
import { Button } from "@/components/ui/button";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuTrigger 
} from "@/components/ui/dropdown-menu";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { useTranslation } from "react-i18next";

interface JobsStatusFilterProps {
  statusFilter: string[];
  setStatusFilter: (statuses: string[]) => void;
}

export const JobsStatusFilter = ({ 
  statusFilter, 
  setStatusFilter 
}: JobsStatusFilterProps) => {
  const [statusMenuOpen, setStatusMenuOpen] = useState(false);
  const [statusSearchTerm, setStatusSearchTerm] = useState("");
  const { t } = useTranslation();

  // Job status options
  const jobStatuses = [
    "open",
    "hold",
    "closed/lost",
    "closed/won"
  ];

  // Toggle status selection
  const toggleStatus = (status: string) => {
    if (statusFilter.includes(status)) {
      setStatusFilter(statusFilter.filter(s => s !== status));
    } else {
      setStatusFilter([...statusFilter, status]);
    }
  };

  // Filter statuses based on search term - case insensitive
  const filteredStatuses = jobStatuses.filter(status => 
    status.toLowerCase().includes(statusSearchTerm.toLowerCase())
  );

  return (
    <DropdownMenu open={statusMenuOpen} onOpenChange={setStatusMenuOpen}>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="outline" 
          className="flex items-center gap-2"
          aria-expanded={statusMenuOpen}
          data-state={statusMenuOpen ? "open" : "closed"}
        >
          <Filter className="h-4 w-4" />
          <span>{t("jobs.status", "Status")}</span>
          {statusFilter.length > 0 && (
            <span className="ml-1 bg-blue-100 text-blue-800 text-xs font-medium px-2 py-0.5 rounded-sm">
              {statusFilter.length}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        className="w-56 p-3 bg-white" 
        align="start"
        onInteractOutside={() => setStatusMenuOpen(false)}
      >
        <div className="space-y-3">
          <div className="relative">
            <Input
              placeholder={t("candidates.filters.statusSearchPlaceholder", "Search statuses...")}
              value={statusSearchTerm}
              onChange={(e) => setStatusSearchTerm(e.target.value)}
              className="text-sm h-8"
            />
          </div>
          
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {filteredStatuses.length === 0 ? (
              <p className="text-sm text-gray-500 py-1">{t("candidates.filters.noStatusesMatch", "No statuses match")}</p>
            ) : (
              filteredStatuses.map(status => (
                <div key={status} className="flex items-center space-x-2">
                  <Checkbox 
                    id={`status-${status}`} 
                    checked={statusFilter.includes(status)}
                    onCheckedChange={() => toggleStatus(status)}
                  />
                  <Label 
                    htmlFor={`status-${status}`}
                    className="text-sm font-normal cursor-pointer flex-1"
                  >
                    {t(`jobs.formWizard.statuses.${status}`, status)}
                  </Label>
                </div>
              ))
            )}
          </div>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}; 